import React from 'react';
import { Button, Spinner } from '@fluentui/react-components';
import { Add16Regular, Delete16Regular, Edit16Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { ProtocolConsumption } from '../../../models/Entities/Protocols/ProtocolConsumption';
import { ProtocolConsumptionConceptValues } from '../../../models/Entities/Protocols/ProtocolConsumptionConceptValues';
import ConsumptionAmountComponent from '../ConsumptionAmount/ConsumptionAmount.component';

// guía
import { GuideTable, GuideTh, guideCellClassName as cell, guideRowClassName } from '../../../theme/components/GuideTable';

// formato
import { amountNoteOf, fmtAmount, fmtCurrency, fmtCurrencyCode, fmtNumber, fmtPercent, hasQuotation, settlementTotalsOf, withholdingTotalsOf } from './ConsumptionDetails.Format';

export interface ValuesGridProps {
    consumption: ProtocolConsumption;
    protocolId: number;
    /** Hay contrato: se pueden agregar, editar y quitar conceptos adicionales. */
    canEditConceptValues: boolean;
    onAddConceptValue: () => void;
    onEditConceptValue: (value: ProtocolConsumptionConceptValues) => void;
    onDeleteConceptValue: (value: ProtocolConsumptionConceptValues) => void;
}

type Tone = 'muted' | 'negative' | 'strong';

/** Celda numérica: importe en tabulares y, debajo, una nota secundaria (porcentaje, moneda, cantidad). */
const Num = ({ value, note, tone }: { value?: string; note?: string; tone?: Tone }) => {
    if (!value) return <span className="cdt-num cdt-num--empty">—</span>;
    return (
        <span className={`cdt-num${tone ? ` cdt-num--${tone}` : ''}`}>
            <span className="cdt-num__value">{value}</span>
            {note && <span className="cdt-num__note">{note}</span>}
        </span>
    );
};

/**
 * Detalle de valores del consumo (guía, grilla nativa): concepto principal con su editor de
 * importe, conceptos adicionales con alta, edición y baja en la misma sección, y fila de total.
 * Con conversión de moneda aparecen además cotización e importe de origen.
 */
export const ConsumptionDetailsValuesGrid = ({ consumption: c, protocolId, canEditConceptValues, onAddConceptValue, onEditConceptValue, onDeleteConceptValue }: ValuesGridProps) => {
    const [t] = useTranslation();
    const showQuotation = hasQuotation(c);
    const totals = settlementTotalsOf(c);
    const withholding = withholdingTotalsOf(c);
    const showAdditional = (c.conceptValues?.length ?? 0) > 0 || canEditConceptValues;
    const numericColumns = 7 + (showQuotation ? 2 : 0);

    return (
        <GuideTable ariaLabel={t('consumptionDetails.valuesDetail')} data-testid="cdt-values-grid" stickyFirstColumn>
            <thead>
                <tr>
                    <GuideTh>{t('consumptionDetails.valuesGrid.concept')}</GuideTh>
                    {showQuotation && <GuideTh align="end">{t('consumptionDetailsGrid.quotation')}</GuideTh>}
                    {showQuotation && <GuideTh align="end">{t('consumptionDetails.valuesGrid.originAmount')}</GuideTh>}
                    <GuideTh align="end">{t('consumptionDetails.valuesGrid.unitAmount')}</GuideTh>
                    <GuideTh align="end">{t('consumptionDetails.valuesGrid.amount')}</GuideTh>
                    <GuideTh align="end">{t('consumptionDetails.valuesGrid.grossValue')}</GuideTh>
                    <GuideTh align="end">{t('consumptionDetails.valuesGrid.withholding')}</GuideTh>
                    <GuideTh align="end">{t('consumptionDetails.valuesGrid.center')}</GuideTh>
                    <GuideTh align="end">{t('consumptionDetails.valuesGrid.medicalTeam')}</GuideTh>
                    <GuideTh align="end">{t('consumptionDetails.valuesGrid.total')}</GuideTh>
                </tr>
            </thead>
            <tbody>
                {/* ── Concepto principal ── */}
                <tr className={guideRowClassName({}, 'cdt-row--main')} data-testid="cdt-row-main">
                    <td>
                        <div className="cdt-concept">
                            <span className="cdt-concept__name">{c.conceptDescription || t('consumptionDetails.valuesGrid.mainConcept')}</span>
                            {c.haveOverheadIncluded && (
                                <span className="cdt-concept__note">Overhead ({fmtNumber(c.overheadPercentage, 0, 3)}%) {fmtAmount(c.overheadAmount)}</span>
                            )}
                            {!c.groupingId && (
                                <div className="cdt-concept__editor">
                                    <ConsumptionAmountComponent ProtocolId={protocolId} ProtocolConsumptionId={c.id} showCurrentValue={false} />
                                </div>
                            )}
                        </div>
                    </td>
                    {showQuotation && <td className={cell('num', 'end')}><Num value={fmtNumber(c.rate, 2, 3)} tone="muted" /></td>}
                    {showQuotation && (
                        <td className={cell('num', 'end')}>
                            <Num value={c.originalAmount !== undefined ? fmtAmount(c.originalAmount) : undefined} note={fmtCurrencyCode(c.originalCurrencyCode)} tone="muted" />
                        </td>
                    )}
                    <td className={cell('num', 'end')}><Num value={fmtAmount(c.unitAmount)} /></td>
                    <td className={cell('num', 'end')}><Num value={fmtAmount(c.amount)} note={amountNoteOf(c)} /></td>
                    <td className={cell('num', 'end')}><Num value={fmtAmount(c.amount)} /></td>
                    <td className={cell('num', 'end')}>
                        <Num value={c.withHolingAmount ? `−${fmtAmount(c.withHolingAmount)}` : undefined} note={c.withHolingPercentage ? fmtPercent(c.withHolingPercentage) : undefined} tone="negative" />
                    </td>
                    <td className={cell('num', 'end')}><Num value={fmtAmount(c.ownerAmountWithholdingTotal)} note={fmtPercent(c.feeDivisionPercentage === 0 ? 100 : c.feeDivisionPercentage)} /></td>
                    <td className={cell('num', 'end')}><Num value={fmtAmount(c.teamAmountWithholdingTotal)} note={fmtPercent(c.teamDivisionPercentage)} /></td>
                    <td className={cell('num', 'end')}><Num value={fmtAmount((c.ownerAmountWithholdingTotal ?? 0) + (c.teamAmountWithholdingTotal ?? 0))} tone="strong" /></td>
                </tr>

                {/* ── Conceptos adicionales: la cabecera de la sección es también donde se agrega ── */}
                {showAdditional && (
                    <tr className={guideRowClassName({ detail: true }, 'cdt-row--section')} data-testid="cdt-row-additional-section">
                        <td colSpan={numericColumns + 1}>
                            <div className="cdt-section-row">
                                <span className="cdt-section-row__title">{t('consumptionDetails.extraConcepts')}</span>
                                {canEditConceptValues && (
                                    <Button appearance="subtle" size="small" icon={<Add16Regular />} onClick={onAddConceptValue} data-testid="cdt-add-concept-value">
                                        {t('consumptionDetails.add')}
                                    </Button>
                                )}
                            </div>
                        </td>
                    </tr>
                )}
                {showAdditional && (c.conceptValues ?? []).map((cv) => {
                    const ownerNet = cv.applyWithholding ? cv.finalOwnerAmount : cv.ownerAmount;
                    const teamNet = cv.applyWithholding ? cv.finalTeamAmount : cv.teamAmount;
                    return (
                        <tr key={cv.id} className={guideRowClassName({}, 'cdt-row--additional')} data-testid={`cdt-row-additional-${cv.id}`}>
                            <td>
                                <div className="cdt-concept cdt-concept--additional">
                                    <span className="cdt-concept__name">{cv.contractConceptName}</span>
                                    {canEditConceptValues && (
                                        <span className="cdt-concept__actions">
                                            <Button appearance="subtle" size="small" icon={<Edit16Regular />} aria-label={`${t('consumptionDetails.editConcept')} ${cv.contractConceptName}`} title={t('consumptionDetails.editConcept')} disabled={cv.isDeleting} onClick={() => onEditConceptValue(cv)} />
                                            <Button appearance="subtle" size="small" icon={cv.isDeleting ? <Spinner size="extra-tiny" /> : <Delete16Regular />} aria-label={`${t('consumptionDetails.removeConcept')} ${cv.contractConceptName}`} title={t('consumptionDetails.removeConcept')} disabled={cv.isDeleting} onClick={() => onDeleteConceptValue(cv)} />
                                        </span>
                                    )}
                                </div>
                            </td>
                            {showQuotation && <td className={cell('num', 'end')}><Num /></td>}
                            {showQuotation && <td className={cell('num', 'end')}><Num /></td>}
                            <td className={cell('num', 'end')}><Num /></td>
                            <td className={cell('num', 'end')}><Num /></td>
                            <td className={cell('num', 'end')}><Num value={fmtAmount(cv.value)} note={fmtPercent(cv.percentValue)} /></td>
                            <td className={cell('num', 'end')}>
                                <Num value={cv.applyWithholding ? `−${fmtAmount(cv.withholdingAmount)}` : undefined} note={cv.applyWithholding ? fmtPercent(cv.withholdingPercentage) : undefined} tone="negative" />
                            </td>
                            <td className={cell('num', 'end')}><Num value={fmtAmount(ownerNet)} note={fmtPercent(cv.feeDivisionPercentage === 0 ? 100 : cv.feeDivisionPercentage)} /></td>
                            <td className={cell('num', 'end')}><Num value={fmtAmount(teamNet)} note={fmtPercent(cv.teamDivisionPercentage)} /></td>
                            <td className={cell('num', 'end')}><Num value={fmtAmount(ownerNet + teamNet)} tone="strong" /></td>
                        </tr>
                    );
                })}

                {/* ── Total ── */}
                <tr className={guideRowClassName({ total: true })} data-testid="cdt-row-total">
                    <td>{t('consumptionDetails.valuesGrid.totalRow')}</td>
                    {showQuotation && <td />}
                    {showQuotation && <td />}
                    <td />
                    <td />
                    <td />
                    <td className={cell('num', 'end')}><Num value={withholding.total ? `−${fmtAmount(withholding.total)}` : undefined} tone="negative" /></td>
                    <td className={cell('num', 'end')}><Num value={fmtAmount(totals.ownerNet)} /></td>
                    <td className={cell('num', 'end')}><Num value={fmtAmount(totals.teamNet)} /></td>
                    <td className={cell('num', 'end', 'amount')}><Num value={fmtCurrency(totals.total, c.currencyCode)} tone="strong" /></td>
                </tr>
            </tbody>
        </GuideTable>
    );
};

export default ConsumptionDetailsValuesGrid;
